import { useState, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import api from '../../services/api';

// DATXALE_FB_LOGIN_FLAG_20260520
// Facebook OAuth public sign-in disabled hasta que Meta apruebe Advanced Access
// para public_profile/email. Mientras tanto solo usuarios con rol en la app pueden
// usar el flow (testers). Cuando Meta apruebe, cambiar a true y rebuildear.
const ENABLE_FACEBOOK_LOGIN = false;
import { motion } from 'framer-motion';
import { TextField, Button, CircularProgress, IconButton, Menu, MenuItem } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Mail, Lock, Eye, EyeOff, Globe, Facebook, Chrome } from 'lucide-react';
import { useLanguage } from '../../i18n/useLanguage';
import { languageNames } from '../../i18n/translations';
import NeuralBrain3D from '../../components/NeuralBrain3D';
import SafeThreeCanvas from '../../components/SafeThreeCanvas';
import { AuthContext } from '../../context/Auth/AuthContext';

const useStyles = makeStyles((theme) => ({
  '@keyframes floatIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(50px) scale(0.9)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
  container: {
    position: 'relative',
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
    background: '#000000',
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
  },
  languageSelector: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 10,
  },
  languageButton: {
    color: '#00d9ff',
    background: 'rgba(0, 217, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(0, 217, 255, 0.3)',
    '&:hover': {
      background: 'rgba(0, 217, 255, 0.2)',
      borderColor: 'rgba(0, 217, 255, 0.5)',
    },
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(2),
  },
  loginCard: {
    background: 'rgba(0, 20, 40, 0.85)',
    backdropFilter: 'blur(40px) saturate(180%)',
    borderRadius: '20px',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(rgba(0, 20, 40, 0.85), rgba(0, 20, 40, 0.85)), linear-gradient(135deg, #00d9ff, #00ff88, #0099ff, #00ffff)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    boxShadow: '0 20px 60px rgba(0, 217, 255, 0.3), 0 0 40px rgba(0, 255, 136, 0.2)',
    padding: theme.spacing(3.5),
    width: '100%',
    maxWidth: '420px',
    position: 'relative',
    overflow: 'visible',
    animation: '$floatIn 0.8s ease-out',
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
      maxWidth: '95%',
      borderRadius: '16px',
    },
    '&:hover': {
      transform: 'translateY(-5px) scale(1.02)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent, rgba(0, 255, 136, 0.5), transparent)',
    },
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  logoIcon: {
    width: '50px',
    height: '50px',
    background: 'linear-gradient(135deg, #00d9ff 0%, #00ff88 100%)',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(0, 217, 255, 0.5)',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      width: '44px',
      height: '44px',
      borderRadius: '12px',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: '-2px',
      borderRadius: '18px',
      padding: '2px',
      background: 'linear-gradient(135deg, #00d9ff, #00ff88)',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      animation: '$pulse 2s ease-in-out infinite',
    },
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.5 },
  },
  title: {
    fontSize: '1.6rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #00d9ff 0%, #00ff88 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: theme.spacing(0.5),
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.4rem',
    },
  },
  subtitle: {
    color: 'rgba(0, 217, 255, 0.8)',
    fontSize: '0.875rem',
    fontWeight: 400,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
    },
  },
  inputWrapper: {
    position: 'relative',
  },
  input: {
    '& .MuiOutlinedInput-root': {
      background: 'rgba(0, 20, 40, 0.5)',
      borderRadius: '10px',
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: 'rgba(0, 217, 255, 0.3)',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 217, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#00d9ff',
        borderWidth: '2px',
        boxShadow: '0 0 20px rgba(0, 217, 255, 0.3)',
      },
      '& input': {
        color: '#ffffff',
        padding: '11px 14px',
        fontSize: '0.9rem',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(0, 217, 255, 0.7)',
      fontSize: '0.9rem',
      transform: 'translate(42px, 12px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
        background: 'rgba(0, 20, 40, 0.9)',
        padding: '0 8px',
      },
      '&.Mui-focused': {
        color: '#00d9ff',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiOutlinedInput-root': {
        '& input': {
          padding: '9px 12px',
          fontSize: '0.85rem',
        },
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.85rem',
        transform: 'translate(38px, 10px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)',
        },
      },
    },
  },
  button: {
    marginTop: theme.spacing(1.5),
    padding: '11px',
    background: 'linear-gradient(135deg, #00d9ff 0%, #00ff88 100%)',
    color: '#000000',
    fontWeight: 600,
    fontSize: '0.95rem',
    textTransform: 'none',
    borderRadius: '10px',
    border: 'none',
    boxShadow: '0 8px 24px rgba(0, 217, 255, 0.5)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      background: 'linear-gradient(135deg, #00b8d4 0%, #00e676 100%)',
      boxShadow: '0 12px 32px rgba(0, 217, 255, 0.6)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&:disabled': {
      background: 'rgba(51, 65, 85, 0.5)',
      boxShadow: 'none',
      color: 'rgba(255, 255, 255, 0.3)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '9px',
      fontSize: '0.9rem',
    },
  },
  eyeIcon: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: 'rgba(0, 217, 255, 0.7)',
    transition: 'color 0.2s',
    '&:hover': {
      color: '#00d9ff',
    },
  },
  alert: {
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    '& .MuiAlert-message': {
      color: '#fca5a5',
    },
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0),
    '&::before, &::after': {
      content: '""',
      flex: 1,
      height: '1px',
      background: 'linear-gradient(90deg, transparent, rgba(0, 217, 255, 0.3), transparent)',
    },
  },
  dividerText: {
    color: 'rgba(0, 217, 255, 0.6)',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  socialButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  socialButton: {
    padding: '10px',
    borderRadius: '10px',
    border: '1px solid rgba(0, 217, 255, 0.3)',
    background: 'rgba(0, 20, 40, 0.5)',
    color: '#ffffff',
    fontWeight: 500,
    fontSize: '0.9rem',
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'rgba(0, 20, 40, 0.7)',
      borderColor: 'rgba(0, 217, 255, 0.5)',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 217, 255, 0.2)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '8px',
      fontSize: '0.85rem',
    },
  },
  facebookButton: {
    '&:hover': {
      borderColor: '#1877f2',
      boxShadow: '0 4px 12px rgba(24, 119, 242, 0.3)',
    },
  },
  googleButton: {
    '&:hover': {
      borderColor: '#4285f4',
      boxShadow: '0 4px 12px rgba(66, 133, 244, 0.3)',
    },
  },
  forgotPassword: {
    textAlign: 'center',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  forgotLink: {
    color: 'rgba(0, 217, 255, 0.8)',
    textDecoration: 'none',
    fontSize: '0.85rem',
    fontWeight: 500,
    transition: 'color 0.2s',
    '&:hover': {
      color: '#00ff88',
      textDecoration: 'underline',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  footer: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    color: 'rgba(0, 217, 255, 0.7)',
    fontSize: '0.8rem',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1.5),
      fontSize: '0.75rem',
    },
  },
  link: {
    color: '#00ff88',
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.2s',
    '&:hover': {
      color: '#00d9ff',
      textDecoration: 'underline',
    },
  },
}));

// Selector de idioma
function LanguageSelector({ language, setLanguage }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectLanguage = (lang) => {
    setLanguage(lang);
    handleClose();
  };

  return (
    <div className={classes.languageSelector}>
      <IconButton
        onClick={handleClick}
        className={classes.languageButton}
        size="small"
      >
        <Globe size={20} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            background: 'rgba(0, 20, 40, 0.95)',
            border: '1px solid rgba(0, 217, 255, 0.3)',
            color: '#ffffff',
          },
        }}
      >
        {Object.entries(languageNames).map(([code, name]) => (
          <MenuItem
            key={code}
            selected={language === code}
            onClick={() => handleSelectLanguage(code)}
            style={{
              color: language === code ? '#00ff88' : '#ffffff',
            }}
          >
            {name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

// Componente principal del Login
function LoginTree() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const { handleLogin } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // DATXALE_OAUTH_HANDSHAKE_20260520
  // Detect ?token=...&social=facebook|google en URL despues del callback OAuth.
  // El backend redirige aqui con el JWT en query param. Guardamos token, llamamos
  // refresh_token para hidratar el user completo, y navegamos a la home.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const social = params.get('social');
    const oauthError = params.get('error');

    if (oauthError) {
      setError(decodeURIComponent(oauthError));
      // Limpia la URL para que F5 no re-dispare el error
      history.replace('/login');
      return;
    }

    if (token && social) {
      setLoading(true);
      localStorage.setItem('token', token);
      api.defaults.headers.Authorization = `Bearer ${token}`;
      // refresh_token devuelve el user payload completo (queues, dueDate, etc.)
      api.post('/auth/refresh_token')
        .then(({ data }) => {
          if (data && data.token) {
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user || {}));
            if (data.user && data.user.profileImage !== undefined) {
              localStorage.setItem('profileImage', data.user.profileImage || '');
            }
            // Forzar reload completo para que useAuth re-monte con la nueva sesion.
            window.location.href = '/';
          } else {
            setError(t.login.errorLogin);
            setLoading(false);
            history.replace('/login');
          }
        })
        .catch(() => {
          setError(t.login.errorLogin);
          setLoading(false);
          localStorage.removeItem('token');
          history.replace('/login');
        });
    }
  }, [location.search, history, t.login.errorLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await handleLogin({ email, password });
      if (result && result.ok === false) {
        setError(result.error || t.login.errorLogin);
      }
    } catch (err) {
      setError(t.login.errorLogin);
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookLogin = () => {
    // DATXALE_OAUTH_HANDSHAKE_20260520
    // Redirect a backend /auth/facebook que redirige a FB OAuth dialog.
    // Tras autorizar, FB -> /auth/facebook/callback -> redirect a /login?token=...
    window.location.href = '/auth/facebook';
  };

  const handleGoogleLogin = () => {
    // DATXALE_OAUTH_HANDSHAKE_20260520
    window.location.href = '/auth/google';
  };

  const handleForgotPassword = () => {
    // TODO: Implementar recuperación de contraseña
    history.push('/forgot-password');
  };

  return (
    <div className={classes.container}>
      {/* Selector de idioma */}
      <LanguageSelector language={language} setLanguage={setLanguage} />

      {/* Canvas 3D de fondo con Neural Brain Network */}
      <div className={classes.canvas}>
        <SafeThreeCanvas fallback={null}>
          <NeuralBrain3D />
        </SafeThreeCanvas>
      </div>

      {/* Contenido del formulario */}
      <div className={classes.content}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className={classes.loginCard}>
            <div className={classes.header}>
              <div className={classes.logo}>
                <div className={classes.logoIcon}>
                  <svg width="44" height="44" viewBox="0 0 200 200" fill="none">
                    <defs>
                      <linearGradient id="kloLg" x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0" stopColor="#00fff0"/>
                        <stop offset="1" stopColor="#1e3a5f"/>
                      </linearGradient>
                      <linearGradient id="kloDot" x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0" stopColor="#a4ffe6"/>
                        <stop offset="1" stopColor="#00fff0"/>
                      </linearGradient>
                    </defs>
                    <g transform="translate(48 50)">
                      <rect x="0" y="0" width="14" height="100" rx="3" fill="url(#kloLg)"/>
                      <path d="M22 50 L72 4 L92 4 L42 50 L92 96 L72 96 Z" fill="url(#kloLg)"/>
                      <circle cx="100" cy="50" r="11" fill="url(#kloDot)"/>
                      <circle cx="100" cy="50" r="18" fill="none" stroke="url(#kloLg)" strokeWidth="1.8" opacity="0.5"/>
                    </g>
                  </svg>
                </div>
              </div>
              <h1 className={classes.title}>{t.login.title}</h1>
              <p className={classes.subtitle}>{t.login.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className={classes.form}>
              <div className={classes.inputWrapper}>
                <TextField
                  fullWidth
                  label={t.login.email}
                  variant="outlined"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={classes.input}
                  InputProps={{
                    startAdornment: (
                      <Mail size={20} style={{ marginRight: 8, color: 'rgba(0, 217, 255, 0.7)' }} />
                    ),
                  }}
                />
              </div>

              <div className={classes.inputWrapper}>
                <TextField
                  fullWidth
                  label={t.login.password}
                  variant="outlined"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={classes.input}
                  InputProps={{
                    startAdornment: (
                      <Lock size={20} style={{ marginRight: 8, color: 'rgba(0, 217, 255, 0.7)' }} />
                    ),
                  }}
                />
                <div
                  className={classes.eyeIcon}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </div>
              </div>

              {error && (
                <Alert severity="error" className={classes.alert}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                className={classes.button}
              >
                {loading ? (
                  <CircularProgress size={24} style={{ color: '#000000' }} />
                ) : (
                  t.login.loginButton
                )}
              </Button>
            </form>

            {/* Enlace de recuperar contraseña */}
            <div className={classes.forgotPassword}>
              <a href="#" onClick={(e) => { e.preventDefault(); handleForgotPassword(); }} className={classes.forgotLink}>
                {t.login.forgotPassword}
              </a>
            </div>

            {/* Divider */}
            <div className={classes.divider}>
              <span className={classes.dividerText}>{t.login.orContinueWith}</span>
            </div>

            {/* Botones sociales */}
            <div className={classes.socialButtons}>
              {ENABLE_FACEBOOK_LOGIN && (
                <Button
                  fullWidth
                  onClick={handleFacebookLogin}
                  className={`${classes.socialButton} ${classes.facebookButton}`}
                >
                  <Facebook size={20} />
                  {t.login.loginWithFacebook}
                </Button>
              )}
              <Button
                fullWidth
                onClick={handleGoogleLogin}
                className={`${classes.socialButton} ${classes.googleButton}`}
              >
                <Chrome size={20} />
                {t.login.loginWithGoogle}
              </Button>
            </div>

            <div className={classes.footer}>
              {t.login.noAccount}{' '}
              <a href="/signup" className={classes.link}>
                {t.login.signupLink}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default LoginTree;
